import { supabase } from '@/lib/supabase';
import { CacheManager } from './cache';
import { fetchProfilesByIds } from './fetchProfilesByIds';
import { logger } from './logger';
import { formatProfileName } from './profileHelpers';

// Helper function to format creation time for display
const formatCreationTime = (createdAt: string): string => {
  try {
    const date = new Date(createdAt);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  } catch (error) {
    return '00:00'; // Fallback to original value if parsing fails
  }
};

// Initialize cache manager
const cache = new CacheManager();

// Type definitions for optimized data structures
export interface OptimizedProfile {
  id: string;
  first_name: string;
  last_name: string;
  profile_image: string | null;
  role: string;
  email?: string;
  background_image?: string | null;
  hours?: number;
}

export interface OptimizedMessage {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  attachments: Array<{ type?: string; url?: string; name?: string }> | null;
  chat_id: string;
  status?: 'sent' | 'deleted'; // Add status field for soft delete
  read_by?: Array<{ user_id: string; read_at: string }>; // Add read_by field for read receipts
}

export interface OptimizedEvent {
  id: string;
  name: string;
  image: string | null;
  location: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  slots: number;
  volunteerCount?: number;
  departments: string[] | null;
  time_intervals: string[] | null;
  is_public: boolean;
  created_by: string;
  created_at: string;
}

/**
 * A `chat_participants → chats → events` embed row as returned by getChats. The
 * select-string parser types `chats` as an array, but it is a to-one embed, so the
 * rows are cast to this shape.
 */
interface ChatParticipantChatRow {
  id: string;
  name: string;
  event_id: string;
  created_at: string;
  events:
    | { image: string; name: string; is_active: boolean; updated_at?: string }
    | Array<{ image: string; name: string; is_active: boolean; updated_at?: string }>
    | null;
}

export interface OptimizedChat {
  id: string;
  name: string;
  event_id: string;
  created_at: string;
  event?: {
    image: string;
    name: string;
    is_active: boolean;
    updated_at?: string;
  } | null;
  lastMessage?: {
    sender: string;
    text: string;
    time: string;
    timestamp: string;
  };
}

export interface OptimizedVolunteer {
  id: string;
  volunteer_id: string;
  event_id: string;
  status: string;
  departments: string[] | null;
  time_slots: string[] | null;
  created_at: string;
  hours?: number;
  certificateCount?: number;
  /** Accepted org membership role when loaded with an organization context. */
  orgMembershipRole?: 'member' | 'admin';
  profiles?: OptimizedProfile;
  events?: Pick<OptimizedEvent, 'id' | 'name' | 'image'>;
}

export interface OptimizedOrganization {
  id: string;
  name: string;
  image: string | null;
  location: string;
  date: string;
  status: string;
  slots: number;
  description: string | null;
  created_at: string;
}

export interface OptimizedCertificate {
  id: string;
  user_id: string;
  title: string;
  organization: string;
  date: string;
  image_url: string;
  is_public: boolean;
  event_id: string | null;
  created_at: string;
}

// Profile Queries
export const OptimizedProfileQueries = {
  // Get single profile with only essential fields
  async getProfile(userId: string): Promise<OptimizedProfile | null> {
    const cacheKey = `profile:${userId}`;
    
    // Check cache first
    const cached = cache.get<OptimizedProfile>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, profile_image, role, email')
      .eq('id', userId)
      .single();

    if (error || !data) return null;

    // Cache for 30 minutes
    cache.set(cacheKey, data, 30 * 60 * 1000);
    return data;
  },

  // Get full profile (for settings/profile pages)
  async getFullProfile(userId: string): Promise<OptimizedProfile | null> {
    const cacheKey = `full-profile:${userId}`;
    
    const cached = cache.get<OptimizedProfile>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, profile_image, role, email, background_image, age, hours, tshirtSize, description, skills, date_of_birth')
      .eq('id', userId)
      .single();

    if (error || !data) return null;

    // Calculate total hours from volunteers table
    let totalHours = 0;
    try {
      const { data: volunteerData, error: volunteerError } = await supabase
        .from('volunteers')
        .select('hours')
        .eq('volunteer_id', userId)
        .eq('is_active', true);
      
      
      if (!volunteerError && volunteerData) {
        totalHours = volunteerData.reduce((sum, volunteer) => sum + (volunteer.hours || 0), 0);
      }
    } catch (error) {
      // Error calculating total hours
    }

    // Add total hours to profile data
    const profileWithHours = {
      ...data,
      hours: totalHours
    };

    // Cache for 15 minutes (shorter since it includes more data)
    cache.set(cacheKey, profileWithHours, 15 * 60 * 1000);
    return profileWithHours;
  },

  // Batch get multiple profiles
  async getMultipleProfiles(userIds: string[]): Promise<OptimizedProfile[]> {
    if (userIds.length === 0) return [];

    // Check cache for each profile
    const uncachedIds: string[] = [];
    const cachedProfiles: OptimizedProfile[] = [];

    for (const userId of userIds) {
      const cached = cache.get<OptimizedProfile>(`profile:${userId}`);
      if (cached) {
        cachedProfiles.push(cached);
      } else {
        uncachedIds.push(userId);
      }
    }

    // Fetch uncached profiles in batch
    if (uncachedIds.length > 0) {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, profile_image, role')
        .in('id', uncachedIds);

      if (!error && data) {
        // Cache each fetched profile
        data.forEach(profile => {
          cache.set(`profile:${profile.id}`, profile, 30 * 60 * 1000);
        });
        cachedProfiles.push(...data);
      }
    }

    return cachedProfiles;
  }
};

// Message Queries
export const OptimizedMessageQueries = {
  // Get paginated messages
  async getMessages(chatId: string, page: number = 0, pageSize: number = 50): Promise<OptimizedMessage[]> {
    
    const cacheKey = `messages:${chatId}:${page}:${pageSize}`;
    
    // Check cache first (shorter TTL for messages)
    const cached = cache.get<OptimizedMessage[]>(cacheKey);
    if (cached) {
      return cached;
    }

    // Get the most recent messages first, then reverse them for display
    const { data, error } = await supabase
      .from('messages')
      .select('id, sender_id, content, created_at, attachments, chat_id, status, read_by')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: false })
      .range(page * pageSize, (page + 1) * pageSize - 1);

    if (error || !data) {
      return [];
    }


    // Reverse the messages so oldest appear first (for chat display)
    const reversedData = [...data].reverse();

    // Cache for 5 minutes (messages change frequently)
    cache.set(cacheKey, reversedData, 5 * 60 * 1000);
    return reversedData;
  },

  // Get latest messages for chat list
  async getLatestMessages(chatIds: string[]): Promise<{ [chatId: string]: OptimizedMessage }> {
    if (chatIds.length === 0) return {};

    const { data, error } = await supabase
      .from('messages')
      .select('id, sender_id, content, created_at, attachments, chat_id, status, read_by')
      .in('chat_id', chatIds)
      .order('created_at', { ascending: false });

    if (error || !data) return {};

    // Get the latest message for each chat
    const latestMessages: { [chatId: string]: OptimizedMessage } = {};
    data.forEach(message => {
      if (!latestMessages[message.chat_id]) {
        latestMessages[message.chat_id] = message;
      }
    });

    return latestMessages;
  }
};

// Event Queries
export const OptimizedEventQueries = {
  // Get events for organizations page
  async getEvents(organizationId?: string, isPublic?: boolean, limit: number = 20, bypassCache: boolean = false): Promise<OptimizedEvent[]> {
    const cacheKey = `events:${organizationId || 'all'}:${isPublic}:${limit}`;
    
    // Skip cache if bypassCache is true or for now to ensure fresh data after fixing query
    const cached = bypassCache ? null : null; // cache.get<OptimizedEvent[]>(cacheKey);
    if (cached) {
      return cached;
    }

    let query = supabase
      .from('events')
      .select(`
        id, 
        name, 
        image, 
        location, 
        start_date, 
        end_date, 
        status, 
        slots, 
        departments, 
        time_intervals, 
        is_public, 
        created_by, 
        created_at
      `)
      .eq('is_active', true) // Only fetch active events
      .order('created_at', { ascending: false })
      .limit(limit);

    if (organizationId) {
      query = query.eq('created_by', organizationId); // Use created_by instead of organization_id
    }

    if (isPublic !== undefined) {
      query = query.eq('is_public', isPublic);
    }

    const { data, error } = await query;

    if (error) {
      return [];
    }

    if (!data) {
      return [];
    }

    // Get volunteer counts for each event
    const eventsWithVolunteerCounts = await Promise.all(
      data.map(async (event) => {
        try {
          const { data: volunteersData } = await supabase
            .from('volunteers')
            .select('id')
            .eq('event_id', event.id)
            .eq('is_active', true);
          
          const volunteerCount = volunteersData ? volunteersData.length : 0;
          
          return {
            ...event,
            volunteerCount
          };
        } catch (error) {
          return {
            ...event,
            volunteerCount: 0
          };
        }
      })
    );

    // Cache for 30 minutes
    cache.set(cacheKey, eventsWithVolunteerCounts, 30 * 60 * 1000);
    return eventsWithVolunteerCounts;
  },

  // Get single event
  async getEvent(eventId: string): Promise<OptimizedEvent | null> {
    const cacheKey = `event:${eventId}`;
    
    const cached = cache.get<OptimizedEvent>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('events')
      .select(`
        id, 
        name, 
        image, 
        location, 
        start_date, 
        end_date, 
        status, 
        slots, 
        departments, 
        time_intervals, 
        is_public, 
        created_by,
        created_at
      `)
      .eq('id', eventId)
      .single();

    if (error || !data) return null;

    // Cache for 30 minutes
    cache.set(cacheKey, data, 30 * 60 * 1000);
    return data;
  }
};

// Chat Queries
export const OptimizedChatQueries = {
  // Get chats for user
  async getChats(userId: string): Promise<OptimizedChat[]> {
    const cacheKey = `chats:${userId}`;
    
    const cached = cache.get<OptimizedChat[]>(cacheKey);
    if (cached) return cached;

    const { data, error } = await supabase
      .from('chat_participants')
      .select(`
        chats:chat_id (
          id,
          name,
          event_id,
          created_at,
          events:event_id (
            image,
            name,
            is_active,
            updated_at,
            end_date,
            grace_period_ends_at
          )
        )
      `)
      .eq('user_id', userId);

    if (error) {
      // If updated_at column doesn't exist, try without it
      if (error.code === '42703') {
        const { data: dataWithoutUpdated, error: error2 } = await supabase
          .from('chat_participants')
          .select(`
            chats:chat_id (
              id,
              name,
              event_id,
              created_at,
              events:event_id (
                image,
                name,
                is_active,
                end_date,
                grace_period_ends_at
              )
            )
          `)
          .eq('user_id', userId);

        if (error2 || !dataWithoutUpdated) return [];

        const chats: OptimizedChat[] = dataWithoutUpdated
          .map(item => item.chats as unknown as ChatParticipantChatRow | null)
          .filter((chat): chat is ChatParticipantChatRow => chat !== null)
          .map((chat) => {
            const eventRow = Array.isArray(chat?.events) ? chat.events[0] : chat?.events;
            return {
              id: chat.id,
              name: chat.name,
              event_id: chat.event_id,
              created_at: chat.created_at,
              event: eventRow
                ? {
                    image: eventRow.image,
                    name: eventRow.name,
                    is_active: eventRow.is_active,
                    updated_at: undefined, // column missing in fallback
                  }
                : null,
            } satisfies OptimizedChat;
          });

        // Fetch last messages for each chat (fallback case)
        const chatsWithLastMessages = await Promise.all(
          chats.map(async (chat) => {
            try {
              // Get the last message for this chat
              const { data: lastMessageData, error: messageError } = await supabase
                .from('messages')
                .select('*')
                .eq('chat_id', chat.id)
                .order('created_at', { ascending: false })
                .limit(1)
                .single();

              if (messageError || !lastMessageData) {
                return {
                  ...chat,
                  lastMessage: {
                    sender: 'System',
                    text: 'No messages yet',
                    time: formatCreationTime(chat.created_at),
                    timestamp: chat.created_at
                  }
                };
              }

              // Fetch sender information
              let senderName = 'Unknown User';
              try {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('first_name, last_name, role')
                  .eq('id', lastMessageData.sender_id)
                  .single();
                
                if (profile) {
                  if (profile.role === 'organization') {
                    senderName = profile.first_name;
                  } else {
                    senderName = formatProfileName(profile.first_name, profile.last_name, 'Unknown User');
                  }
                }
              } catch (error) {
                // Use fallback sender name
              }

              // Handle message text based on status, content and attachments
              let messageText = lastMessageData.content;
              
              // Check if message is deleted FIRST
              if (lastMessageData.status === 'deleted') {
                messageText = 'This message was deleted';
              } else if (!messageText || messageText.trim() === '') {
                // If content is empty or null, check attachments
                if (lastMessageData.attachments && lastMessageData.attachments.length > 0) {
                  const attachment = lastMessageData.attachments[0];
                  if (attachment.type === 'image') {
                    messageText = 'sent a photo';
                  } else if (attachment.type === 'document') {
                    messageText = 'sent a pdf';
                  } else {
                    messageText = 'sent an attachment';
                  }
                } else {
                  messageText = 'sent a message';
                }
              } else if (lastMessageData.attachments && lastMessageData.attachments.length > 0) {
                // If there's both content and attachments, prioritize attachments
                const attachment = lastMessageData.attachments[0];
                if (attachment.type === 'image') {
                  messageText = 'sent a photo';
                } else if (attachment.type === 'document') {
                  messageText = 'sent a pdf';
                } else {
                  messageText = 'sent an attachment';
                }
              }

              return {
                ...chat,
                lastMessage: {
                  sender: senderName,
                  text: messageText,
                  time: new Date(lastMessageData.created_at).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  }),
                  timestamp: lastMessageData.created_at
                }
              };
            } catch (error) {
              return {
                ...chat,
                lastMessage: {
                  sender: 'System',
                  text: 'No messages yet',
                  time: formatCreationTime(chat.created_at),
                  timestamp: chat.created_at
                }
              };
            }
          })
        );

        cache.set(cacheKey, chatsWithLastMessages, 15 * 60 * 1000);
        return chatsWithLastMessages;
      }
      return [];
    }

    if (!data) return [];

    const chats: OptimizedChat[] = data
      .map(item => item.chats as unknown as ChatParticipantChatRow | null)
      .filter((chat): chat is ChatParticipantChatRow => chat !== null)
      .map((chat) => {
        const eventRow = Array.isArray(chat?.events) ? chat.events[0] : chat?.events;
        return {
          id: chat.id,
          name: chat.name,
          event_id: chat.event_id,
          created_at: chat.created_at,
          event: eventRow
            ? {
                image: eventRow.image,
                name: eventRow.name,
                is_active: eventRow.is_active,
                updated_at: eventRow.updated_at,
              }
            : null,
        } satisfies OptimizedChat;
      });

    // Fetch last messages for each chat
    const chatsWithLastMessages = await Promise.all(
      chats.map(async (chat) => {
        try {
          // Get the last message for this chat
          const { data: lastMessageData, error: messageError } = await supabase
            .from('messages')
            .select('*')
            .eq('chat_id', chat.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (messageError || !lastMessageData) {
            return {
              ...chat,
              lastMessage: {
                sender: 'System',
                text: 'No messages yet',
                time: formatCreationTime(chat.created_at),
                timestamp: chat.created_at
              }
            };
          }

          // Fetch sender information
          let senderName = 'Unknown User';
          try {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, role')
              .eq('id', lastMessageData.sender_id)
              .single();
            
            if (profile) {
              if (profile.role === 'organization') {
                senderName = profile.first_name;
              } else {
                senderName = formatProfileName(profile.first_name, profile.last_name, 'Unknown User');
              }
            }
          } catch (error) {
            // Use fallback sender name
          }

          // Handle message text based on status, content and attachments
          let messageText = lastMessageData.content;
          
          // Check if message is deleted FIRST
          if (lastMessageData.status === 'deleted') {
            messageText = 'This message was deleted';
          } else if (!messageText || messageText.trim() === '') {
            // If content is empty or null, check attachments
            if (lastMessageData.attachments && lastMessageData.attachments.length > 0) {
              const attachment = lastMessageData.attachments[0];
              if (attachment.type === 'image') {
                messageText = 'sent a photo';
              } else if (attachment.type === 'document') {
                messageText = 'sent a pdf';
              } else {
                messageText = 'sent an attachment';
              }
            } else {
              messageText = 'sent a message';
            }
          } else if (lastMessageData.attachments && lastMessageData.attachments.length > 0) {
            // If there's both content and attachments, prioritize attachments
            const attachment = lastMessageData.attachments[0];
            if (attachment.type === 'image') {
              messageText = 'sent a photo';
            } else if (attachment.type === 'document') {
              messageText = 'sent a pdf';
            } else {
              messageText = 'sent an attachment';
            }
          }

          return {
            ...chat,
            lastMessage: {
              sender: senderName,
              text: messageText,
              time: new Date(lastMessageData.created_at).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              }),
              timestamp: lastMessageData.created_at
            }
          };
        } catch (error) {
          return {
            ...chat,
            lastMessage: {
              sender: 'System',
              text: 'No messages yet',
              time: formatCreationTime(chat.created_at),
              timestamp: chat.created_at
            }
          };
        }
      })
    );

    // Cache for 15 minutes
    cache.set(cacheKey, chatsWithLastMessages, 15 * 60 * 1000);
    return chatsWithLastMessages;
  }
};

export interface GetVolunteersResult {
  volunteers: OptimizedVolunteer[];
  /** The volunteer rows loaded but their profiles did not — names/avatars are placeholders. */
  profilesUnavailable: boolean;
}

// Volunteer Queries
export const OptimizedVolunteerQueries = {
  // Get volunteers for organization
  async getVolunteers(eventId?: string, organizationId?: string): Promise<GetVolunteersResult> {
    // Never fetch volunteers without an eventId – avoid loading all volunteers globally
    if (!eventId) return { volunteers: [], profilesUnavailable: false };

    const cacheKey = `volunteers:${eventId}:${organizationId || 'all'}`;

    // Only successful reads are cached (see the bottom of this function), so a hit is complete.
    const cached = cache.get<OptimizedVolunteer[]>(cacheKey);
    if (cached) return { volunteers: cached, profilesUnavailable: false };

    // Base volunteers query – mirror columns used in the React Native screen
    let query = supabase
      .from('volunteers')
      .select(
        `
        id,
        volunteer_id,
        event_id,
        created_at,
        department,
        time_slots,
        hours,
        role,
        is_active
      `,
      )
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (eventId) {
      query = query.eq('event_id', eventId);
    }

    const { data, error } = await query;

    if (error || !data) {
      if (error) {
        logger.error('getVolunteers: volunteers read failed', {
          code: error.code,
          message: error.message,
          details: error.details,
          eventId,
        });
      }
      return { volunteers: [], profilesUnavailable: false };
    }

    // Fetch related profiles in batch
    const volunteerIds = data.map((v) => v.volunteer_id).filter(Boolean);
    const eventIds = data.map((v) => v.event_id).filter(Boolean);

    const { profilesById, failed: profilesUnavailable } = await fetchProfilesByIds<OptimizedProfile>(
      volunteerIds,
      'id, first_name, last_name, profile_image, role',
      'getVolunteers',
    );

    let eventsById: Record<string, Pick<OptimizedEvent, 'id' | 'name' | 'image'>> = {};

    if (eventIds.length) {
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, name, image')
        .in('id', eventIds);

      if (eventsError) {
        logger.error('getVolunteers: events read failed', {
          code: eventsError.code,
          message: eventsError.message,
        });
      }

      if (eventsData) {
        eventsById = eventsData.reduce((acc, event) => {
          acc[event.id] = {
            id: event.id,
            name: event.name,
            image: event.image,
          };
          return acc;
        }, {} as Record<string, Pick<OptimizedEvent, 'id' | 'name' | 'image'>>);
      }
    }

    const certificateCountByKey: Record<string, number> = {};
    if (volunteerIds.length && eventIds.length) {
      const { data: certData, error: certError } = await supabase
        .from('certificates')
        .select('user_id, event_id')
        .in('user_id', volunteerIds)
        .in('event_id', eventIds);
      if (certError) {
        logger.error('getVolunteers: certificates read failed', {
          code: certError.code,
          message: certError.message,
        });
      }
      if (certData) {
        certData.forEach((c: { user_id: string; event_id: string }) => {
          const key = `${c.user_id}:${c.event_id}`;
          certificateCountByKey[key] = (certificateCountByKey[key] || 0) + 1;
        });
      }
    }

    const orgMembershipRoleByVolunteerId: Record<string, 'member' | 'admin'> = {};
    if (organizationId && volunteerIds.length) {
      const { data: membershipData, error: membershipError } = await supabase
        .from('organization_memberships')
        .select('volunteer_id, role')
        .eq('organization_id', organizationId)
        .eq('status', 'accepted')
        .in('volunteer_id', volunteerIds);

      if (membershipError) {
        logger.error('getVolunteers: memberships read failed', {
          code: membershipError.code,
          message: membershipError.message,
        });
      }

      for (const row of membershipData ?? []) {
        const m = row as { volunteer_id: string; role: string };
        orgMembershipRoleByVolunteerId[m.volunteer_id] = m.role === 'admin' ? 'admin' : 'member';
      }
    }

    type VolunteerRow = {
      id: string;
      volunteer_id: string;
      event_id: string;
      created_at: string;
      department: string | null;
      time_slots: string[] | string | null;
      hours: number | null;
      is_active: boolean;
    };
    const transformed: OptimizedVolunteer[] = data.map((row: VolunteerRow) => ({
      id: row.id,
      volunteer_id: row.volunteer_id,
      event_id: row.event_id,
      status: row.is_active ? 'active' : 'inactive',
      departments: row.department ? [row.department] : [],
      time_slots: Array.isArray(row.time_slots)
        ? row.time_slots
        : row.time_slots
          ? [row.time_slots]
          : [],
      created_at: row.created_at,
      hours: row.hours ?? 0,
      certificateCount: certificateCountByKey[`${row.volunteer_id}:${row.event_id}`] ?? 0,
      orgMembershipRole: orgMembershipRoleByVolunteerId[row.volunteer_id],
      profiles: profilesById[row.volunteer_id] ?? undefined,
      events: eventsById[row.event_id] ?? undefined,
    }));

    // Cache for 15 minutes — but never cache a list whose names failed to load, or the
    // placeholder "Volunteer" rows would stick around for the rest of the session.
    if (!profilesUnavailable) {
      cache.set(cacheKey, transformed, 15 * 60 * 1000);
    }
    return { volunteers: transformed, profilesUnavailable };
  }
};

// Certificate Queries
export const OptimizedCertificateQueries = {
  // Get certificates for user
  async getCertificates(userId: string, isPublic?: boolean, eventId?: string): Promise<OptimizedCertificate[]> {
    const cacheKey = `certificates:${userId}:${isPublic}:${eventId || 'all'}`;
    
    const cached = cache.get<OptimizedCertificate[]>(cacheKey);
    if (cached) return cached;

    let query = supabase
      .from('certificates')
      .select('id, user_id, title, organization, date, image_url, is_public, event_id, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (isPublic !== undefined) {
      query = query.eq('is_public', isPublic);
    }

    if (eventId) {
      query = query.eq('event_id', eventId);
    }

    const { data, error } = await query;

    if (error || !data) return [];

    // Cache for 30 minutes
    cache.set(cacheKey, data, 30 * 60 * 1000);
    return data;
  }
};

// Cache invalidation helpers
export const CacheInvalidation = {
  // Invalidate user-related caches
  invalidateUser(userId: string) {
    cache.delete(`profile:${userId}`);
    cache.delete(`full-profile:${userId}`);
    cache.delete(`chats:${userId}`);
    cache.delete(`certificates:${userId}:true:all`);
    cache.delete(`certificates:${userId}:false:all`);
    cache.delete(`certificates:${userId}:undefined:all`);
  },

  // Invalidate chat-related caches
  invalidateChat(chatId: string) {
    // Clear all message pages for this chat
    const cacheKeys = cache.getAllKeys();
    const deletedKeys = [];
    cacheKeys.forEach(key => {
      if (key.startsWith(`messages:${chatId}:`)) {
        cache.delete(key);
        deletedKeys.push(key);
      }
    });
  },

  // Invalidate event-related caches
  invalidateEvent(eventId: string) {
    cache.delete(`event:${eventId}`);
    // Clear events lists that might contain this event
    const cacheKeys = cache.getAllKeys();
    cacheKeys.forEach(key => {
      if (key.startsWith('events:')) {
        cache.delete(key);
      }
    });
  },

  // Invalidate volunteer lists for an event (e.g. after department change)
  invalidateVolunteers(eventId: string) {
    const cacheKeys = cache.getAllKeys();
    cacheKeys.forEach((key) => {
      if (key.startsWith(`volunteers:${eventId}:`)) {
        cache.delete(key);
      }
    });
  },

  // Patch a single volunteer's department in cached lists without invalidating
  patchVolunteerDepartment(eventId: string, volunteerId: string, department: string | null) {
    const nextDepartments = department ? [department] : [];
    const cacheKeys = cache.getAllKeys();
    cacheKeys.forEach((key) => {
      if (!key.startsWith(`volunteers:${eventId}:`)) return;
      const cached = cache.get<OptimizedVolunteer[]>(key);
      if (!cached) return;
      const updated = cached.map((v) =>
        v.id === volunteerId ? { ...v, departments: nextDepartments } : v,
      );
      cache.set(key, updated, 15 * 60 * 1000);
    });
  },

  // Clear all caches
  clearAll() {
    cache.clear();
  }
};

// Performance monitoring
export const QueryPerformance = {
  async measureQuery<T>(queryName: string, queryFn: () => Promise<T>): Promise<T> {
    const startTime = performance.now();
    
    const result = await queryFn();
    const duration = performance.now() - startTime;

    // Log slow queries
    if (duration > 1000) {
      logger.warn(`Slow query: ${queryName} took ${Math.round(duration)}ms`);
    }

    return result;
  }
};

// Export all optimized queries
export const OptimizedQueries = {
  profiles: OptimizedProfileQueries,
  messages: OptimizedMessageQueries,
  events: OptimizedEventQueries,
  chats: OptimizedChatQueries,
  volunteers: OptimizedVolunteerQueries,
  certificates: OptimizedCertificateQueries,
  cache: CacheInvalidation,
  performance: QueryPerformance
};
